import { getDonglesApi, getErrorMessage, isKnownError } from '@/castapi/helpers';
import type {
  DongleActivityDto,
  DongleLicenseDto,
  DongleReauthCodeDto,
  HttpExceptionDto,
  InsertDongleByOwnerDto,
  ProductInfoDto,
} from '@/castapi';
import { AppLogger } from '@/logger';
import type { IActionParams } from '@/store/modules/index';

const logger = new AppLogger('dongles state');

interface IDonglesState {
  dongles: ProductInfoDto[];
  donglesLoading: boolean;
  donglesLoadError?: string;
  currentDongle?: ProductInfoDto & { emergencyCode?: string };
  currentDongleChanging: boolean;
  currentDongleChangeError?: string;
  isRenewUpgrade: boolean;
  dongleInserting: boolean;
  dongleInsertError?: string;
  dongleActivitiesLoading: boolean;
  dongleActivitiesLoadError?: string;
  dongleActivities: DongleActivityDto[];
  dongleLicensesLoading: boolean;
  dongleLicensesLoadError?: string;
  dongleLicenses: DongleLicenseDto[];
  dongleReauthCodeGenerating: boolean;
  dongleReauthCodeError?: string;
  dongleReauthCode?: string;
  dongleEmergencyCodeGenerating: boolean;
  dongleEmergencyCodeError?: string;
  reauthRequestSubmitting: boolean;
  reauthRequestError?: string;
  reauthRequestErrorStatus?: number;
  latestReauthCode?: DongleReauthCodeDto;
  latestReauthCodeLoading: boolean;
}

const initialState: () => IDonglesState = () => ({
  dongles: [],
  donglesLoading: false,
  donglesLoadError: undefined,
  currentDongle: undefined,
  currentDongleChanging: false,
  currentDongleChangeError: undefined,
  isRenewUpgrade: true,
  dongleInserting: false,
  dongleInsertError: undefined,
  dongleActivitiesLoading: false,
  dongleActivitiesLoadError: undefined,
  dongleActivities: [],
  dongleReauthCodeGenerating: false,
  dongleReauthCodeError: undefined,
  dongleReauthCode: undefined,
  dongleEmergencyCodeGenerating: false,
  dongleEmergencyCodeError: undefined,
  dongleLicenses: [],
  dongleLicensesLoading: false,
  dongleLicensesLoadError: undefined,
  reauthRequestSubmitting: false,
  reauthRequestError: undefined,
  reauthRequestErrorStatus: undefined,
  latestReauthCode: undefined,
  latestReauthCodeLoading: false,
});

type ActionParams = IActionParams<IDonglesState>;

export default {
  namespaced: true,
  state: initialState,
  mutations: {
    DONGLES_LOADING(state: IDonglesState): void {
      state.donglesLoadError = undefined;
      state.donglesLoading = true;
    },
    DONGLES_LOADED(state: IDonglesState, payload: ProductInfoDto[]): void {
      state.dongles = payload;
      state.donglesLoading = false;
      state.currentDongle =
        state.dongles.find(dongle => dongle.dongleId === state.currentDongle?.dongleId) || undefined;
    },
    DONGLES_LOAD_ERROR(state: IDonglesState, payload: Error): void {
      state.donglesLoadError = getErrorMessage(payload);
      state.donglesLoading = false;
    },
    DONGLE_CHANGING(state: IDonglesState): void {
      state.currentDongleChanging = true;
      state.currentDongleChangeError = undefined;
    },
    DONGLE_CHANGED(state: IDonglesState): void {
      state.currentDongleChanging = false;
    },
    DONGLE_CHANGE_ERROR(state: IDonglesState, payload: Error): void {
      state.currentDongleChangeError = getErrorMessage(payload);
      state.currentDongleChanging = false;
    },
    SELECT_DONGLE(state: IDonglesState, dongle: ProductInfoDto): void {
      state.currentDongle = dongle;
    },
    DONGLE_INSERTING(state: IDonglesState): void {
      state.dongleInserting = true;
      state.dongleInsertError = undefined;
    },
    DONGLE_INSERTED(state: IDonglesState): void {
      state.dongleInserting = false;
    },
    DONGLE_INSERT_ERROR(state: IDonglesState, payload: Error): void {
      state.dongleInsertError = getErrorMessage(payload);
      state.dongleInserting = false;
    },
    DONGLE_ACTIVITIES_LOADING(state: IDonglesState): void {
      state.dongleActivities = [];
      state.dongleActivitiesLoadError = undefined;
      state.dongleActivitiesLoading = true;
    },
    DONGLE_ACTIVITIES_LOADED(state: IDonglesState, activities: DongleActivityDto[]): void {
      state.dongleActivities = activities;
      state.dongleActivitiesLoading = false;
    },
    DONGLE_ACTIVITIES_LOAD_ERROR(state: IDonglesState, payload: Error): void {
      state.dongleActivitiesLoading = false;
      state.dongleActivitiesLoadError = getErrorMessage(payload);
    },
    DONGLE_REAUTH_CODE_GENERATING(state: IDonglesState): void {
      state.dongleReauthCodeGenerating = true;
      state.dongleReauthCodeError = undefined;
      state.dongleReauthCode = undefined;
    },
    DONGLE_REAUTH_CODE_GENERATED(state: IDonglesState, code: string): void {
      state.dongleReauthCodeGenerating = false;
      state.dongleReauthCode = code;
    },
    DONGLE_REAUTH_CODE_ERROR(state: IDonglesState, payload: Error): void {
      state.dongleReauthCodeGenerating = false;
      state.dongleReauthCodeError = getErrorMessage(payload);
    },
    DROP_DONGLE_REAUTH_CODE(state: IDonglesState): void {
      state.dongleReauthCodeGenerating = false;
      state.dongleReauthCodeError = undefined;
      state.dongleReauthCode = undefined;
      state.reauthRequestSubmitting = false;
      state.reauthRequestError = undefined;
      state.reauthRequestErrorStatus = undefined;
      state.latestReauthCode = undefined;
    },
    REAUTH_REQUEST_SUBMITTING(state: IDonglesState): void {
      state.reauthRequestSubmitting = true;
      state.reauthRequestError = undefined;
      state.reauthRequestErrorStatus = undefined;
      state.dongleReauthCode = undefined;
    },
    REAUTH_REQUEST_SUCCESS(state: IDonglesState, code: string): void {
      state.reauthRequestSubmitting = false;
      state.dongleReauthCode = code;
    },
    REAUTH_REQUEST_ERROR(state: IDonglesState, payload: HttpExceptionDto): void {
      state.reauthRequestSubmitting = false;
      state.reauthRequestError = getErrorMessage(payload);
      state.reauthRequestErrorStatus = payload?.status;
    },
    LATEST_REAUTH_CODE_LOADING(state: IDonglesState): void {
      state.latestReauthCodeLoading = true;
    },
    LATEST_REAUTH_CODE_LOADED(state: IDonglesState, payload: DongleReauthCodeDto): void {
      state.latestReauthCodeLoading = false;
      state.latestReauthCode = payload;
    },
    LATEST_REAUTH_CODE_ERROR(state: IDonglesState): void {
      state.latestReauthCodeLoading = false;
      state.latestReauthCode = undefined;
    },
    DONGLE_EMERGENCY_CODE_GENERATING(state: IDonglesState): void {
      state.dongleEmergencyCodeGenerating = true;
      state.dongleEmergencyCodeError = undefined;
    },
    DONGLE_EMERGENCY_CODE_GENERATED(state: IDonglesState, emergencyCode: string): void {
      state.dongleEmergencyCodeGenerating = false;
      state.currentDongle = { ...(state.currentDongle as ProductInfoDto), emergencyCode };
    },
    DONGLE_EMERGENCY_CODE_ERROR(state: IDonglesState, payload: Error): void {
      state.dongleEmergencyCodeGenerating = false;
      state.dongleEmergencyCodeError = getErrorMessage(payload);
    },

    // eslint-disable-next-line unused-imports/no-unused-vars -- skipcq: JS-0356
    AUTO_RENEW_CHANGED(state: IDonglesState, payload: { cloudProductId: number; autoRenew: boolean }): void {
      //needed for subscription
      state.donglesLoading = true;
    },
    RESET_STATE(state: IDonglesState): void {
      Object.assign(state, initialState());
    },
  },
  actions: {
    async loadDongles({ commit, rootGetters }: ActionParams): Promise<void> {
      try {
        commit('DONGLES_LOADING');
        const response = await getDonglesApi(rootGetters['login/accessToken']).donglesControllerFindAllByOrganization(
          rootGetters['organizations/organizationId'],
        );
        commit('DONGLES_LOADED', response.data);
      } catch (error) {
        commit('DONGLES_LOAD_ERROR', error);
        logger.captureStoreError('loadDongles', error);
      }
    },
    async insertDongle({ commit, dispatch, rootGetters }: ActionParams, data: InsertDongleByOwnerDto): Promise<void> {
      commit('DONGLE_INSERTING');
      try {
        await getDonglesApi(rootGetters['login/accessToken']).donglesControllerInsertDongleByOwner(data);
        commit('DONGLE_INSERTED');
        await dispatch('loadDongles');
      } catch (error) {
        commit('DONGLE_INSERT_ERROR', error);
        logger.captureStoreError('insertDongle', error, { data });
      }
    },
    async assignDongle(
      { commit, dispatch, rootGetters }: ActionParams,
      { dongleId, userId }: { dongleId: number; userId: number },
    ): Promise<void> {
      commit('DONGLE_CHANGING');
      try {
        await getDonglesApi(rootGetters['login/accessToken']).donglesControllerAssignDongle({ dongleId, userId });
        commit('DONGLE_CHANGED');
        await dispatch('loadDongles');
      } catch (error) {
        commit('DONGLE_CHANGE_ERROR', error);
        logger.captureStoreError('assignDongle', error, { dongleId, userId });
      }
    },
    async unAssignDongle({ commit, dispatch, rootGetters }: ActionParams, dongleId: number): Promise<void> {
      commit('DONGLE_CHANGING');
      try {
        await getDonglesApi(rootGetters['login/accessToken']).donglesControllerUnAssignDongle({ dongleId });
        commit('DONGLE_CHANGED');
        await dispatch('loadDongles');
      } catch (error) {
        commit('DONGLE_CHANGE_ERROR', error);
        logger.captureStoreError('unAssignDongle', error, { dongleId });
      }
    },
    async loadDongleActivities({ commit, rootGetters }: ActionParams, dongleId: number): Promise<void> {
      commit('DONGLE_ACTIVITIES_LOADING');
      try {
        const response = await getDonglesApi(rootGetters['login/accessToken']).donglesControllerGetDongleActivities(
          dongleId,
        );
        commit('DONGLE_ACTIVITIES_LOADED', response.data);
      } catch (error) {
        commit('DONGLE_ACTIVITIES_LOAD_ERROR', error);
        logger.captureStoreError('loadDongleActivities', error, { dongleId });
      }
    },
    async generateDongleReauthCode({ commit, rootGetters }: ActionParams, dongleSerialCode: string): Promise<void> {
      commit('DONGLE_REAUTH_CODE_GENERATING');
      try {
        const response = await getDonglesApi(rootGetters['login/accessToken']).donglesControllerGetDongleReauthCode(
          dongleSerialCode,
        );
        commit('DONGLE_REAUTH_CODE_GENERATED', response.data);
      } catch (error) {
        commit('DONGLE_REAUTH_CODE_ERROR', error);
        if (isKnownError(error as HttpExceptionDto, 400, 'Dongle not assigned to any user')) {
          return;
        }
        logger.captureStoreError('generateDongleReauthCode', error, { dongleSerialCode });
      }
    },
    async submitReauthRequestCode(
      { commit, rootGetters }: ActionParams,
      data: { dongleId: number; requestCode: string },
    ): Promise<void> {
      commit('REAUTH_REQUEST_SUBMITTING');
      try {
        const response = await getDonglesApi(
          rootGetters['login/accessToken'],
        ).donglesControllerGetDongleReauthCodeFromRequestCode(data);
        commit('REAUTH_REQUEST_SUCCESS', response.data);
      } catch (error) {
        commit('REAUTH_REQUEST_ERROR', error);
        const status = (error as HttpExceptionDto)?.status;
        // A wrong dongle, a typo or the attempt limit are all rendered on the card, not captured.
        if (status && [400, 409, 429].includes(status)) {
          return;
        }
        logger.captureStoreError('submitReauthRequestCode', error, { dongleId: data.dongleId });
      }
    },
    async loadDongleLatestReauthCode({ commit, rootGetters }: ActionParams, dongleId: number): Promise<void> {
      commit('LATEST_REAUTH_CODE_LOADING');
      try {
        const response = await getDonglesApi(
          rootGetters['login/accessToken'],
        ).donglesControllerGetDongleLatestReauthCode(dongleId);
        commit('LATEST_REAUTH_CODE_LOADED', response.data);
      } catch (error) {
        // No code yet is the normal case for this audience, so don't surface it as an error.
        commit('LATEST_REAUTH_CODE_ERROR');
        logger.captureStoreError('loadDongleLatestReauthCode', error, { dongleId });
      }
    },
    async generateDongleEmergencyCode({ commit, rootGetters }: ActionParams, dongleId: number): Promise<void> {
      commit('DONGLE_EMERGENCY_CODE_GENERATING');
      try {
        const response = await getDonglesApi(rootGetters['login/accessToken']).donglesControllerGetDongleEmergencyCode(
          dongleId,
        );
        commit('DONGLE_EMERGENCY_CODE_GENERATED', response.data);
      } catch (error) {
        commit('DONGLE_EMERGENCY_CODE_ERROR', error);
        logger.captureStoreError('generateDongleEmergencyCode', error, { dongleId });
      }
    },
    async changeAutoRenew(
      { commit, dispatch, rootGetters }: ActionParams,
      data: {
        dongleId: number;
        autoRenew: boolean;
      },
    ): Promise<void> {
      const { dongleId, autoRenew } = data;
      commit('DONGLES_LOADING');
      try {
        await getDonglesApi(rootGetters['login/accessToken']).donglesControllerChangeAutoRenew({
          dongleId,
          autoRenew,
        });
        commit('AUTO_RENEW_CHANGED', { dongleId, autoRenew });
      } catch (error) {
        commit('DONGLES_LOAD_ERROR', error);
        logger.captureStoreError('loadDongles', error);
      }
      await dispatch('loadDongles');
      await dispatch('loadDongleActivities', dongleId);
    },
    async dropRenewalDate({ commit, dispatch, rootGetters }: ActionParams, dongleId: number): Promise<void> {
      commit('DONGLES_LOADING');
      try {
        await getDonglesApi(rootGetters['login/accessToken']).donglesControllerDropRenewalDate({
          dongleId,
        });
      } catch (error) {
        commit('DONGLES_LOAD_ERROR', error);
        logger.captureStoreError('dropRenewalDate', error);
      }
      await dispatch('loadDongles');
    },
    async cancelAutoRenewDisabling({ commit, dispatch, rootGetters }: ActionParams, dongleId: number): Promise<void> {
      commit('DONGLES_LOADING');
      try {
        await getDonglesApi(rootGetters['login/accessToken']).donglesControllerCancelAutoRenewDisabling({
          dongleId,
        });
      } catch (error) {
        commit('DONGLES_LOAD_ERROR', error);
        logger.captureStoreError('cancelAutoRenewDisabling', error);
      }
      await dispatch('loadDongles');
    },
    resetState({ commit }: ActionParams): void {
      commit('RESET_STATE');
    },
  },
  getters: {
    dongles: (state: IDonglesState): ProductInfoDto[] => state.dongles,
    donglesLoading: (state: IDonglesState): boolean => state.donglesLoading,
    donglesLoadError: (state: IDonglesState): undefined | string => state.donglesLoadError,
    currentDongle: (state: IDonglesState): undefined | (ProductInfoDto & { emergencyCode?: string }) =>
      state.currentDongle,
    currentDongleSerialCode: (state: IDonglesState): undefined | string => state.currentDongle?.serialCode,
    currentDongleId: (state: IDonglesState): undefined | number => state.currentDongle?.dongleId,
    currentDongleAutoRenewDisabledByUser: (state: IDonglesState): undefined | boolean =>
      state.currentDongle?.autoRenewDisabledByUser,
    // dongleInserting: (state: IDonglesState) => state.dongleInserting,
    dongleInsertError: (state: IDonglesState): undefined | string => state.dongleInsertError,
    dongleActivities: (state: IDonglesState): DongleActivityDto[] => state.dongleActivities,
    dongleActivitiesLoading: (state: IDonglesState): boolean => state.dongleActivitiesLoading,
    dongleActivitiesLoadError: (state: IDonglesState): undefined | string => state.dongleActivitiesLoadError,
    dongleReauthCodeGenerating: (state: IDonglesState): boolean => state.dongleReauthCodeGenerating,
    dongleReauthCodeError: (state: IDonglesState): undefined | string => state.dongleReauthCodeError,
    dongleReauthCode: (state: IDonglesState): undefined | string => state.dongleReauthCode,
    reauthRequestSubmitting: (state: IDonglesState): boolean => state.reauthRequestSubmitting,
    reauthRequestError: (state: IDonglesState): undefined | string => state.reauthRequestError,
    reauthRequestErrorStatus: (state: IDonglesState): undefined | number => state.reauthRequestErrorStatus,
    latestReauthCode: (state: IDonglesState): undefined | DongleReauthCodeDto => state.latestReauthCode,
    latestReauthCodeLoading: (state: IDonglesState): boolean => state.latestReauthCodeLoading,
    dongleEmergencyCodeGenerating: (state: IDonglesState): boolean => state.dongleEmergencyCodeGenerating,
    dongleEmergencyCodeError: (state: IDonglesState): undefined | string => state.dongleEmergencyCodeError,
  },
};
